import React, { useState, useEffect, useMemo, useRef  } from "react";
import * as Styled from "./styled";
import Sidemenu from "../../components/sidemenu";
import { functionService } from "../../lib/functions";
import { Select, DatePicker, Dropdown, Menu, Button, Modal, Tooltip, Input, Drawer } from "antd";
import { message } from "antd";
import { CopyOutlined, DownOutlined, MoreOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import ProfileComp from "../profileView/ProfileComp";
import { Switch } from "antd";


const { RangePicker } = DatePicker;

/* ─────────────────────────────────────────────
   Helper: truncate text with max length
───────────────────────────────────────────── */
const truncateText = (text, maxLength = 25) => {
  if (!text) return "-";
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

/* ─────────────────────────────────────────────
   Helper: faceted/constraint-based dropdown options.
   Given the raw filterOptions rows, returns the unique
   sorted values for `targetKey`, filtered by every
   constraint in `constraints` EXCEPT the target's own
   current selection (constraints intentionally omits it).
───────────────────────────────────────────── */
const getFacetOptions = (rows, targetKey, constraints) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const filtered = rows.filter((row) =>
    Object.entries(constraints).every(([key, value]) => {
      if (!value) return true; // no constraint selected for this key
      return row[key] === value;
    })
  );
  return [...new Set(filtered.map((row) => row[targetKey]).filter(Boolean))].sort();
};

/* ─────────────────────────────────────────────
   Shared pagination styles
───────────────────────────────────────────── */
const paginationStyles = {
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 4px",
    borderTop: "1px solid #F0F0F0",
    marginTop: "8px",
  },
  leftSection: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
    color: "#6B7280",
  },
  pageControls: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  pageBtn: (active) => ({
    minWidth: "34px",
    height: "34px",
    padding: "0 10px",
    borderRadius: "8px",
    border: active ? "none" : "1px solid #E5E7EB",
    background: active ? "#4F46E5" : "#FFFFFF",
    color: active ? "#FFFFFF" : "#374151",
    fontWeight: active ? 600 : 400,
    fontSize: "13px",
    cursor: "pointer",
    transition: "all 0.15s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: active ? "0 1px 4px rgba(79,70,229,0.3)" : "none",
  }),
  ellipsis: {
    width: "34px",
    height: "34px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#9CA3AF",
    fontSize: "14px",
    userSelect: "none",
  },
  navBtn: (disabled) => ({
    height: "34px",
    padding: "0 14px",
    borderRadius: "8px",
    border: "1px solid #E5E7EB",
    background: disabled ? "#F9FAFB" : "#FFFFFF",
    color: disabled ? "#D1D5DB" : "#374151",
    fontWeight: 500,
    fontSize: "13px",
    cursor: disabled ? "not-allowed" : "pointer",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    transition: "all 0.15s ease",
  }),
};

const ProfileSentTable = () => {
  const [jobListings, setJobListings] = useState([]);
  const [recruiterOptions, setRecruiterOptions] = useState([]);
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const [companyList, setCompanyList] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);

  const [selectedCompanyShortlistStatus, setSelectedCompanyShortlistStatus] = useState(null);
  const [selectedAssessmentStatus, setSelectedAssessmentStatus] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSourcedDate, setSelectedSourcedDate] = useState(null);
  const [filteredJobListings, setFilteredJobListings] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [selectedAction, setSelectedAction] = useState(null);
  const [userId, setUserId] = useState(null);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [modalData, setModalData] = useState({ id: "", email: "", firstName: "", lastName: "" });
  const [viewData, setViewData] = useState(null);

  const [selectedRows, setSelectedRows] = useState([]);
  const [reviewerRemark, setReviewerRemark] = useState("");
  const [isRemarkModalVisible, setIsRemarkModalVisible] = useState(false);

  const [selectedSubAccount, setSelectedSubAccount] = useState(null);
  const [selectedEndClient, setSelectedEndClient] = useState(null);

  // NOTE: companyOptions / roleOptions / subAccountOptions / endClientOptions
  // are no longer stored in state. They are derived below via useMemo from
  // `filterOptions` + the OTHER selected filters (faceted/constraint-based
  // filtering), so they can never go stale and never need manual syncing.
  const [filterOptions, setFilterOptions] = useState([]);
  const [isCompactView, setIsCompactView] = useState(false);
  const [isCompactViewHovered, setIsCompactViewHovered] = useState(false);

  // Active/inactive recruiter toggle — same as ReviewerTable
  const [activeRecruitersOnlyHovered, setActiveRecruitersOnlyHovered] = useState(false);
  const [activeRecruitersOnly, setActiveRecruitersOnly] = useState(true);

  // Email & name search state
  const [searchText, setSearchText] = useState("");

  const [selectedDebriefStatus, setSelectedDebriefStatus] = useState();
  const [selectedDebriefDate, setSelectedDebriefDate] = useState(null);

  // Filter states
  const [selectedCandidateStatus, setSelectedCandidateStatus] = useState([]);
  const [showOnlyPending, setShowOnlyPending] = useState(false);
  const [showOnlySent, setShowOnlySent] = useState(false);
  const [showOnlyNotSent, setShowOnlyNotSent] = useState(false);
  const [selectedProfileSentDate, setSelectedProfileSentDate] = useState(null);

  // Drawer state
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [drawerCandidate, setDrawerCandidate] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [updateProfileSent, setUpdateProfileSent] = useState(null);
  const [updateProfileSentDate, setUpdateProfileSentDate] = useState(null);
  const [updateCandidateStatus, setUpdateCandidateStatus] = useState(null);
  const [updateCandidateStatusChangedDate, setUpdateCandidateStatusChangedDate] = useState(null);
  const [updateClientRemark, setUpdateClientRemark] = useState("");
  const [updateLoading, setUpdateLoading] = useState(false);

  const [client_feedback, setClientFeedback] = useState("");

  const [interviewDateTime, setInterviewDateTime] = useState(null);
  const [dateRange, setDateRange] = useState([]);

  const fullCandidateStatusOptions = [
    "Yet to be shortlisted",
    "Discussion Round",
    "Pre-Interview Back Out",

    // R1
    "Screen Select - Yet to be Interviewed (R1)",
    "Screen Reject",
    "Interview Scheduled (R1)",
    "During Interview Back Out (R1)",
    "Interviewed - Feedback Pending (R1)",
    "Interviewed - Rejected (R1)",

    // R2
    "Yet to be Interviewed (R2)",
    "Interview Scheduled (R2)",
    "During Interview Back Out (R2)",
    "Interviewed - Feedback Pending (R2)",
    "Interviewed - Rejected (R2)",

    // R3
    "Yet to be Interviewed (R3)",
    "Interview Scheduled (R3)",
    "During Interview Back Out (R3)",
    "Interviewed - Feedback Pending (R3)",
    "Interviewed - Rejected (R3)",

    // R4
    "Yet to be Interviewed (R4)",
    "Interview Scheduled (R4)",
    "During Interview Back Out (R4)",
    "Interviewed - Feedback Pending (R4)",
    "Interviewed - Rejected (R4)",
    "Post Interview Back Out",

    // Final Stage
    "Final Select",
    "Final Reject",
    "Pre Offer Back Out",

    // Offer Stage
    "Offer yet to be released",
    "Offer released",
    "Post Offer Back Out",

    // Joining Stage
    "Offer Accepted - Yet to join",
    "Offer Accepted - Joined",
    "Offer Accepted - Not Joined",
    "Offer Rejected - Not joined",

    // Other Outcomes
    "Position on hold/closed",
    "Change in requirement from client",
    "Duplicate Profile",
    "Profile Reshared"
  ];
  const interviewStatuses = [
    "Interview Scheduled (R1)",
    "Interview Scheduled (R2)",
    "Interview Scheduled (R3)",
    "Interview Scheduled (R4)",
  ];

  const statusFlow = {
    SCREENING: [
      "Yet to be shortlisted",
      "Discussion Round",
      "Pre-Interview Back Out",
      "Screen Select - Yet to be Interviewed (R1)",
      "Interview Scheduled (R1)"
    ],

    R1: [
      "Screen Reject",
      "Interview Scheduled (R1)",
      "During Interview Back Out (R1)",
      "Interviewed - Feedback Pending (R1)",
      "Interviewed - Rejected (R1)",
      "Yet to be Interviewed (R2)",
      "Interview Scheduled (R2)"
    ],

    R2: [
      "Interview Scheduled (R2)",
      "During Interview Back Out (R2)",
      "Interviewed - Feedback Pending (R2)",
      "Interviewed - Rejected (R2)",
      "Yet to be Interviewed (R3)",
      "Interview Scheduled (R3)"
    ],

    R3: [
      "Interview Scheduled (R3)",
      "During Interview Back Out (R3)",
      "Interviewed - Feedback Pending (R3)",
      "Interviewed - Rejected (R3)",
      "Yet to be Interviewed (R4)",
      "Interview Scheduled (R4)"
    ],

    R4: [
      "Interview Scheduled (R4)",
      "During Interview Back Out (R4)",
      "Interviewed - Feedback Pending (R4)",
      "Interviewed - Rejected (R4)"
    ],

    DR: [
      "Discussion Round",
      "Screen Select - Yet to be Interviewed (R1)",
      "Interview Scheduled (R1)"
    ],

    // These are available from every stage
    ALWAYS_AVAILABLE: [
      "Yet to be shortlisted",
      "Discussion Round",
      "Pre-Interview Back Out",

      // R1
      "Screen Select - Yet to be Interviewed (R1)",
      "Screen Reject",
      "Interview Scheduled (R1)",

      // Final Stage
      "Post Interview Back Out",
      "Final Select",
      "Final Reject",
      "Pre Offer Back Out",

      // Offer Stage
      "Offer yet to be released",
      "Offer released",
      "Post Offer Back Out",

      // Joining Stage
      "Offer Accepted - Yet to join",
      "Offer Accepted - Joined",
      "Offer Accepted - Not Joined",
      "Offer Rejected - Not joined",

      // Other Outcomes
      "Position on hold/closed",
      "Change in requirement from client",
      "Duplicate Profile",
      "Profile Reshared"
    ]
  };

  const globalAvailableStatuses = [
    "Yet to be shortlisted",
    "Discussion Round",
    "Pre-Interview Back Out",

    // R1
    "Screen Select - Yet to be Interviewed (R1)",
    "Screen Reject",
    "Interview Scheduled (R1)",

    // Final Stage
    "Post Interview Back Out",
    "Final Select",
    "Final Reject",
    "Pre Offer Back Out",

    // Offer Stage
    "Offer yet to be released",
    "Offer released",
    "Post Offer Back Out",

    // Joining Stage
    "Offer Accepted - Yet to join",
    "Offer Accepted - Joined",
    "Offer Accepted - Not Joined",
    "Offer Rejected - Not joined",

    // Other Outcomes
    "Position on hold/closed",
    "Change in requirement from client",
    "Duplicate Profile",
    "Profile Reshared"
  ];
const isFirstFilterEffect = useRef(true);

  const getStatusStage = (status) => {
    if (!status) return "SCREENING";
    if (status === "Discussion Round") return "DR";
    // Yet to be interviewed belongs to previous round
    if (status === "Yet to be Interviewed (R2)") return "R1";
    if (status === "Yet to be Interviewed (R3)") return "R2";
    if (status === "Yet to be Interviewed (R4)") return "R3";

    // Normal round statuses
    if (status.includes("(R1)")) return "R1";
    if (status.includes("(R2)")) return "R2";
    if (status.includes("(R3)")) return "R3";
    if (status.includes("(R4)")) return "R4";

    // Final statuses belong to R4
    if (
      [
        "Post Interview Back Out",
        "Final Select",
        "Final Reject"
      ].includes(status)
    ) {
      return "R4";
    }

    return "SCREENING";
  };


  const currentStatus =
    updateCandidateStatus ||
    drawerCandidate?.candidate_current_status;

  const currentStage = getStatusStage(currentStatus);

  const allowedStatuses = [
    ...(statusFlow[currentStage] || []),
    ...statusFlow.ALWAYS_AVAILABLE
  ];

  const currentStatusIndex =
    fullCandidateStatusOptions.indexOf(currentStatus);

  const isCurrentStatusGlobal =
    statusFlow.ALWAYS_AVAILABLE.includes(currentStatus);

  const candidateStatusOptions = [
    ...fullCandidateStatusOptions
  ];

  const copyDebriefToClipboard = (debriefText) => {
    if (!debriefText) {
      message.error("No debrief text to copy");
      return;
    }
    navigator.clipboard.writeText(debriefText).then(() => {
      message.success("Debrief copied successfully");
    }).catch(() => {
      message.error("Failed to copy");
    });
  };

  const getStatusChangedDateFromHistory = (status, statusHistory) => {
    if (!status || !Array.isArray(statusHistory)) return null;

    const matchingEntries = statusHistory.filter((entry) => entry.status === status && entry.changed_at);
    if (matchingEntries.length === 0) return null;

    const latestEntry = matchingEntries.reduce((latest, entry) => {
      if (!latest) return entry;
      return dayjs(entry.changed_at).isAfter(dayjs(latest.changed_at)) ? entry : latest;
    }, null);

    return latestEntry?.changed_at ? dayjs(latestEntry.changed_at) : null;
  };

  const resolveStatusChangedDate = (status, statusHistory, fallbackDate = null) => {
    return getStatusChangedDateFromHistory(status, statusHistory)
      || (fallbackDate ? dayjs(fallbackDate) : null)
      || (status ? dayjs() : null);
  };

  const getCompanyName = async () => {
    const response = await functionService.get("demand/demand-companies-list");
    if (response?.response?.data?.data?.length > 0) {
      setCompanyList(response.response.data.data);
    }
  };

  const recruiterSearch = async () => {
    try {
      let res = await functionService.get(`demand/recruiters-list`);
      let apiData = res?.response?.data?.data;
      const optionsWithNames = apiData.map((item) => ({ ...item, recruiter_name: item.label }));
      setRecruiterOptions(optionsWithNames);
    } catch (error) {
      console.error("Error fetching recruiters list:", error);
    }
  };

  /* ─────────────────────────────────────────────
     Backend-driven filter options.
     Only stores the raw relationship rows now — the four
     dropdown option lists are derived below via useMemo.
  ───────────────────────────────────────────── */
  const getFilterOptions = async () => {
    try {
      const response = await functionService.get("demand/demand-filter-list");

      if (response?.response?.data?.data) {
        setFilterOptions(response.response.data.data);
      }
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  };

  /* ─────────────────────────────────────────────
     Faceted/constraint-based dropdown options.

     Each dropdown's valid values are computed from
     `filterOptions` constrained by the OTHER three
     currently selected filters — never by its own
     current selection. Whenever any one of the four
     filters changes, all four recompute automatically
     because they're plain derived values (useMemo),
     not separately-managed state.
  ───────────────────────────────────────────── */
  const companyOptions = useMemo(
    () =>
      getFacetOptions(filterOptions, "company_name", {
        role_title: selectedRole,
        sub_account: selectedSubAccount,
        end_client: selectedEndClient,
      }),
    [filterOptions, selectedRole, selectedSubAccount, selectedEndClient]
  );

  const roleOptions = useMemo(
    () =>
      getFacetOptions(filterOptions, "role_title", {
        company_name: selectedCompany,
        sub_account: selectedSubAccount,
        end_client: selectedEndClient,
      }),
    [filterOptions, selectedCompany, selectedSubAccount, selectedEndClient]
  );

  const subAccountOptions = useMemo(
    () =>
      getFacetOptions(filterOptions, "sub_account", {
        company_name: selectedCompany,
        role_title: selectedRole,
        end_client: selectedEndClient,
      }),
    [filterOptions, selectedCompany, selectedRole, selectedEndClient]
  );

  const endClientOptions = useMemo(
    () =>
      getFacetOptions(filterOptions, "end_client", {
        company_name: selectedCompany,
        role_title: selectedRole,
        sub_account: selectedSubAccount,
      }),
    [filterOptions, selectedCompany, selectedRole, selectedSubAccount]
  );

  /* ─────────────────────────────────────────────
     Stale-selection handling.

     If a selected value is no longer present in its
     OWN valid option list (because a sibling filter
     changed and narrowed things down), clear just that
     one selection — never blindly clear the others.
     Each effect is keyed off the derived options array,
     so it only fires when that dropdown's valid set
     actually changes.
  ───────────────────────────────────────────── */
  useEffect(() => {
    if (selectedCompany && !companyOptions.includes(selectedCompany)) {
      setSelectedCompany(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyOptions]);

  useEffect(() => {
    if (selectedRole && !roleOptions.includes(selectedRole)) {
      setSelectedRole(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleOptions]);

  useEffect(() => {
    if (selectedSubAccount && !subAccountOptions.includes(selectedSubAccount)) {
      setSelectedSubAccount(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subAccountOptions]);

  useEffect(() => {
    if (selectedEndClient && !endClientOptions.includes(selectedEndClient)) {
      setSelectedEndClient(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endClientOptions]);

  /* ─────────────────────────────────────────────
     Backend-driven fetch
  ───────────────────────────────────────────── */
  const fetchProfileSentListings = async (page = 1, activeFilters = {}) => {
    setLoading(true);
    try {
      const filters = { page, limit: itemsPerPage, ...activeFilters };
      const demandDetails = await functionService.getProfileSentListings(filters);
      if (demandDetails?.status) {
        const responseData = demandDetails.response.data.data;
        // Support both { data: [], total: n } and a flat array (with total_count on each row)
        const listings = Array.isArray(responseData) ? responseData : (responseData?.data ?? []);
        const total = responseData?.total ?? listings[0]?.total_count ?? listings.length;

        setJobListings(listings);
        setFilteredJobListings(listings);
        setTotalCount(Number(total) || 0);
      } else {
        console.error("Error fetching profile sent details:", demandDetails?.error);
      }
    } catch (error) {
      console.error("Error in fetchProfileSentListings:", error);
    } finally {
      setLoading(false);
    }
  };

  /* ─────────────────────────────────────────────
     Build the filter payload sent to the backend.
  ───────────────────────────────────────────── */
  const buildFilters = () => ({
    ...(searchText && { candidate_search: searchText }),
    ...(selectedCompany && { company_name: selectedCompany }),
    ...(selectedRole && { role_title: selectedRole }),
    ...(selectedSubAccount && { sub_account: selectedSubAccount }),
    ...(selectedEndClient && { end_client: selectedEndClient }),
    ...(selectedRecruiter && { recruiter_name: selectedRecruiter }),
    ...(selectedAssessmentStatus && { assessment_status: selectedAssessmentStatus }),
    ...(selectedCandidateStatus.length > 0 && { candidate_current_status: selectedCandidateStatus }),
    ...(selectedProfileSentDate && { profile_sent_date: selectedProfileSentDate.format("YYYY-MM-DD") }),
    ...(selectedDebriefStatus && { debrief_status: selectedDebriefStatus }),
    ...(selectedDebriefDate && { debrief_date: selectedDebriefDate.format("YYYY-MM-DD") }),
    ...(selectedSourcedDate && { sourced_date: selectedSourcedDate.format("YYYY-MM-DD") }),
    ...(selectedDate && { reviewed_date: selectedDate.format("YYYY-MM-DD") }),
    ...(dateRange?.length === 2 && {
      sourced_date_from: dateRange[0].format("YYYY-MM-DD"),
      sourced_date_to: dateRange[1].format("YYYY-MM-DD"),
    }),
    // profile_sent is a tri-state filter: pending / yes / no.
    ...(showOnlyPending && { profile_sent: "pending" }),
    ...(showOnlySent && { profile_sent: "yes" }),
    ...(showOnlyNotSent && { profile_sent: "no" }),
    // Active/inactive recruiter toggle — always sent, two-state, same as ReviewerTable
    recruiter_active_filter: activeRecruitersOnly ? "active" : "inactive",
  });

  // 1. Initial load
  useEffect(() => {
    getCompanyName();
    recruiterSearch();
    getFilterOptions();
    // fetchProfileSentListings(1, {});
  }, []);

  // 2. Filters changed → debounced fetch, reset to page 1
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     fetchProfileSentListings(1, buildFilters());
  //     setCurrentPage(1);
  //   }, 300);
  //   return () => clearTimeout(timer);
  // }, [
  //   searchText,
  //   selectedCompany, selectedRole, selectedSubAccount, selectedEndClient,
  //   selectedRecruiter, selectedAssessmentStatus, selectedCandidateStatus,
  //   selectedProfileSentDate, selectedDebriefStatus, selectedDebriefDate,
  //   selectedSourcedDate, selectedDate, dateRange,
  //   showOnlyPending, showOnlySent, showOnlyNotSent, activeRecruitersOnly,
  // ]);

  useEffect(() => {
    if (isFirstFilterEffect.current) {
        isFirstFilterEffect.current = false;
        return;
    }

    const timer = setTimeout(() => {
        fetchProfileSentListings(1, buildFilters());
        setCurrentPage(1);
    }, 300);

    return () => clearTimeout(timer);
}, [
    searchText,
    selectedCompany,
    selectedRole,
    selectedSubAccount,
    selectedEndClient,
    selectedRecruiter,
    selectedAssessmentStatus,
    selectedCandidateStatus,
    selectedProfileSentDate,
    selectedDebriefStatus,
    selectedDebriefDate,
    selectedSourcedDate,
    selectedDate,
    dateRange,
    showOnlyPending,
    showOnlySent,
    showOnlyNotSent,
    activeRecruitersOnly,
]);

  // 3. Page or page size changed
  useEffect(() => {
    fetchProfileSentListings(currentPage, buildFilters());
  }, [currentPage, itemsPerPage]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const paginatedListings = Array.isArray(filteredJobListings) ? filteredJobListings : [];
  const showEmptyState = paginatedListings.length === 0;

  /* ── Pagination page numbers builder ─────────── */
  const buildPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    if (currentPage > 3) pages.push("...");
    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(totalPages - 1, currentPage + 1);
      i++
    ) {
      pages.push(i);
    }
    if (currentPage < totalPages - 2) pages.push("...");
    pages.push(totalPages);
    return pages;
  };

  /* ─────────────────────────────────────────────
     Download Assessment Report
  ───────────────────────────────────────────── */
  const downloadAssessmentReport = async (item) => {
    setDownloadingId(item.assessment_id);
    try {
      const response = await functionService.getReportWithResume(
        `demand/download-assessment-report/${item.assessment_id}`
      );
      const blob = new Blob([response], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const name = `${item.first_name ? item.first_name + "_" : ""}${item.last_name ? item.last_name + "_" : ""}assessment_report`.replace(/\./g, "");
      a.download = `${name}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading assessment report:", error);
      alert("Failed to download report. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  const downloadCVAndReport = async (item) => {
    setDownloadingId(item.assessment_id);
    try {
      const response = await functionService.getReportWithResume(
        `demand/download-assessment-report/${item.assessment_id}?candidate_id=${item.id}`
      );
      const blob = new Blob([response], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const name = `${item.first_name ? item.first_name + "_" : ""}${item.last_name ? item.last_name + "_" : ""}cv_and_report`.replace(/\./g, "");
      a.download = `${name}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading CV & report:", error);
      alert("Failed to download CV & report. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  const downloadKyndrylReport = async (item) => {
    setDownloadingId(item.assessment_id);
    try {
      const response = await functionService.getReportWithResume(
        `demand/download-assessment-report/${item.assessment_id}?report_type=half`
      );
      const blob = new Blob([response], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const name = `${item.first_name ? item.first_name + "_" : ""}${item.last_name ? item.last_name + "_" : ""}kyndryl_assessment_report`.replace(/\./g, "");
      a.download = `${name}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading Kyndryl report:", error);
      alert("Failed to download Kyndryl report. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  const openDrawer = (item) => {
    setDrawerCandidate(item);
    setUpdateProfileSent(item.profile_sent ?? null);
    setUpdateProfileSentDate(item.profile_sent_date ? dayjs(item.profile_sent_date) : null);
    const currentStatus = item.candidate_current_status ?? null;
    setUpdateCandidateStatus(currentStatus);
    setUpdateCandidateStatusChangedDate(
      resolveStatusChangedDate(currentStatus, item.status_history, item.candidate_current_status_time)
    );
    setClientFeedback(item.client_feedback ?? "");
    setUpdateClientRemark(item.client_remark ?? "");
    setIsDrawerVisible(true);
  };

  const closeDrawer = () => {
    setIsDrawerVisible(false);
    setDrawerCandidate(null);
    setClientFeedback("");
    setUpdateClientRemark("");
  };

  const handleUpdateSubmit = async () => {
    if (!drawerCandidate?.id) return;

    const hasStatus = Boolean(updateCandidateStatus);
    const hasStatusChangedDate = Boolean(updateCandidateStatusChangedDate);
    if (hasStatus !== hasStatusChangedDate) {
      alert("Candidate Current Status and Status Changed Date must both be filled together.");
      return;
    }
    if (showInterviewDateTime && !interviewDateTime) {
      alert("Please select Interview Date & Time.");
      return;
    }

    setUpdateLoading(true);
    try {
      const payload = {
        profile_sent: updateProfileSent,
        profile_sent_date: updateProfileSentDate ? updateProfileSentDate.toISOString() : null,
        candidate_current_status: updateCandidateStatus,
        candidate_current_status_time: updateCandidateStatusChangedDate
          ? updateCandidateStatusChangedDate.toISOString()
          : null,
        client_feedback: client_feedback,
        client_remark: updateClientRemark,
        interview_datetime: interviewDateTime
          ? interviewDateTime.toISOString()
          : null,
      };
      const res = await functionService.post(
        `demand/update-profile-sent-status/${drawerCandidate.id}`,
        payload
      );
      if (res?.response?.data?.status || res?.status) {
        // Patch the row in-place on the current page (server-paginated list),
        // then re-fetch in the background to keep counts/filters consistent
        // in case the update moved this row out of the active filter set.
        setJobListings((prev) =>
          prev.map((row) =>
            row.id === drawerCandidate.id
              ? {
                ...row,
                profile_sent: updateProfileSent,
                profile_sent_date: updateProfileSentDate ? updateProfileSentDate.toISOString() : null,
                candidate_current_status: updateCandidateStatus,
                candidate_current_status_time: updateCandidateStatusChangedDate
                  ? updateCandidateStatusChangedDate.toISOString()
                  : null,
                client_feedback: client_feedback,
                client_remark: updateClientRemark,
              }
              : row
          )
        );
        setFilteredJobListings((prev) =>
          prev.map((row) =>
            row.id === drawerCandidate.id
              ? {
                ...row,
                profile_sent: updateProfileSent,
                profile_sent_date: updateProfileSentDate ? updateProfileSentDate.toISOString() : null,
                candidate_current_status: updateCandidateStatus,
                candidate_current_status_time: updateCandidateStatusChangedDate
                  ? updateCandidateStatusChangedDate.toISOString()
                  : null,
                client_feedback: client_feedback,
                client_remark: updateClientRemark,
              }
              : row
          )
        );
        closeDrawer();
        // Re-sync with the server so filters/pagination/counts stay accurate
        fetchProfileSentListings(currentPage, buildFilters());
      } else {
        console.error("Update failed:", res);
        alert("Update failed. Please try again.");
      }
    } catch (error) {
      console.error("Error updating profile sent status:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setUpdateLoading(false);
    }
  };

  const menu = (item) => (
    <Menu onClick={(e) => handleMenuClick(e, item)}>
      {item.assessment_status?.toUpperCase() === "SUBMITTED" && (
        <>
          <Menu.Item key="download" disabled={downloadingId === item.assessment_id}>
            {downloadingId === item.assessment_id ? "Downloading..." : "Download Assessment Report"}
          </Menu.Item>
          <Menu.Item key="downloadHalf" disabled={downloadingId === item.assessment_id}>
            {downloadingId === item.assessment_id ? "Downloading..." : "Kyndryl Assessment Report"}
          </Menu.Item>
          <Menu.Item key="downloadCVAndReport" disabled={downloadingId === item.assessment_id}>
            {downloadingId === item.assessment_id ? "Downloading..." : "Download CV & Report"}
          </Menu.Item>
        </>
      )}
      <Menu.Item key="updateProfileSent">Update Profile Info</Menu.Item>
    </Menu>
  );

  const handleMenuClick = (e, item) => {
    setUserId(item.id);
    switch (e.key) {
      case "download":
        downloadAssessmentReport(item);
        break;
      case "downloadHalf":
        downloadKyndrylReport(item);
        break;
      case "downloadCVAndReport":
        downloadCVAndReport(item);
        break;
      case "updateProfileSent":
        openDrawer(item);
        break;
      default:
        break;
    }
  };

  const updateStatusWithRemark = async () => {
    if (selectedAction === "approved" || selectedAction === "rejected") {
      try {
        const candidateStatus = await functionService.updateCandidateStatus(userId, {
          status: selectedAction,
          remark: reviewerRemark,
        });
        if (candidateStatus.status) {
          await fetchProfileSentListings(currentPage, buildFilters());
        } else {
          console.error("Error updating candidate status:", candidateStatus.error);
        }
      } catch (error) {
        console.error("Error in updateStatusWithRemark:", error);
      }
    }
    setIsRemarkModalVisible(false);
    setReviewerRemark("");
  };

  const handleRowSelect = (row) => {
    setSelectedRows((prev) =>
      prev.includes(row) ? prev.filter((r) => r !== row) : [...prev, row]
    );
  };

  const handleSelectAll = (isChecked) => {
    setSelectedRows(isChecked ? activeList : []);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setIsViewModalVisible(false);
  };

  const profile = {
    name: `${viewData?.first_name} ${viewData?.last_name}`,
    contact: viewData?.primary_no,
    email: viewData?.email,
    exp: `${viewData?.total_work_exp / 12} years ${viewData?.total_work_exp % 12} month`,
    salary: viewData?.current_ctc,
    score: 92,
    imageUrl: "https://media.istockphoto.com/id/513727339/photo/head-and-shoulders-mans-silhouette.jpg?s=612x612&w=0&k=20&c=aAVL1IT9GT4URtLjS98515G5e3Qc4c7Np2EvKXBywMQ=",
    cvUrl: viewData?.cv_url,
  };

  const handleIconClick = (data, viewItem) => {
    setModalData({ status: data });
    if (data === "View") {
      setViewData(viewItem);
      setIsViewModalVisible(true);
    } else {
      setIsModalVisible(true);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not available";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
      return "Invalid date";
    }
  };

  const activeList = paginatedListings;

  /* ─────────────────────────────────────────────
     Export
  ───────────────────────────────────────────── */
  const exportToCSV = async () => {
    let baseData = selectedRows.length > 0 ? selectedRows : [];

    if (selectedRows.length === 0) {
      try {
        const result = await functionService.getProfileSentListings({
          ...buildFilters(), 
          page: 1,
          limit: 100000,
          export: true,
        });

        if (result.status) {
          const responseData = result.response?.data?.data;
          baseData = Array.isArray(responseData)
            ? responseData
            : (responseData?.data ?? []);
        } else {
          console.error("Failed to fetch all data for export");
          return;
        }
      } catch (error) {
        console.error("Error exporting CSV:", error);
        return;
      }
    }

    // Rest of your existing CSV code remains exactly the same

    const headers = [
      "Date of Sourcing",
      "Date of Review",
      "Profile Sent",
      "Candidate Current Status",
      "Client's Feedback",
      "Client's Remark",
      "Date of Sharing with Client",
      "Status Updated Date",
      "Recruiter Name",
      "Account Name",
      "Sub-Account",
      "End Client",
      "Role",
      "Candidate Name",
      "Candidate Email ID",
      "Candidate Mobile Number",
      "Total Exp",
      "Relevant Exp",
      "Current Company",
      "Current Location",
      "Preferred Location",
      "Current CTC (LPA)",
      "Expected CTC (LPA)",
      "Notice Period",
      "Notice Status",
      "Last Working Day",
      "Available From",
      "Offer in Hand (Y/N)",
      "Offered CTC (LPA)",
      "Joining Date of Offer",
      "Date of Birth",
      "Highest Qualification",
      "Recruiter's Remarks",
      "Reviewer's Remarks",
      "Assessment Status",
      "Tech Score",
      "Red Flag",
      "Qualify",
      "Resume Link",
      "Debrief View",
    ];

    const compactHeaders = [
      "Account Name",
      "Sub Account",
      "End Client",
      "Date of Sharing",
      "Role",
      "Recruiter",
      "Candidate Name",
      "Candidate Email",
      "Candidate Contact",
      "Candidate Status",
      "Last Status Updated",
    ];

    const escape = (val) => {
      const str = val === null || val === undefined ? "-" : String(val);
      return str.includes(",") || str.includes('"') || str.includes("\n")
        ? `"${str.replace(/"/g, '""')}"`
        : str;
    };

    const csvHeaders = isCompactView ? compactHeaders : headers;
    const csvRows = [csvHeaders.join(",")];

    baseData.forEach((item) => {
      let row = [];

      // =========================
      // COMPACT VIEW
      // =========================
      if (isCompactView) {
        row = [
          escape(item.company_name || "-"),
          escape(item.sub_account || "-"),
          escape(item.end_client || "-"),
          escape(
            item.profile_sent_date ? formatDate(item.profile_sent_date) : "-"
          ),
          escape(item.role_title || "-"),
          escape(item.recruiter_name || "-"),
          escape(
            `${item.first_name || ""} ${item.last_name || ""}`.trim() || "-"
          ),
          escape(item.email || "-"),
          escape(item.primary_no || "-"),
          escape(item.candidate_current_status || "-"),
          escape(
            item.profile_status_updated_date
              ? formatDate(item.profile_status_updated_date)
              : "-"
          ),
        ];
      }

      // =========================
      // DETAILED VIEW
      // =========================
      else {
        row = [
          escape(formatDate(item.created_at)),
          escape(
            item.review_status === "pending"
              ? "Pending..."
              : formatDate(item.updated_at)
          ),
          escape(
            item.profile_sent === null || item.profile_sent === undefined
              ? "Pending"
              : item.profile_sent
                ? "Yes"
                : "No"
          ),
          escape(item.candidate_current_status || "Pending"),
          escape(item.client_feedback || "-"),
          escape(item.client_remark || "-"),
          escape(
            item.profile_sent_date
              ? formatDate(item.profile_sent_date)
              : item.profile_sent === null || item.profile_sent === undefined
                ? "Pending"
                : "-"
          ),
          escape(
            item.profile_status_updated_date
              ? formatDate(item.profile_status_updated_date)
              : "-"
          ),
          escape(item.recruiter_name || "-"),
          escape(item.company_name || "-"),
          escape(item.sub_account || "-"),
          escape(item.end_client || "-"),
          escape(item.role_title || "-"),
          escape(
            `${item.first_name || ""} ${item.last_name || ""}`.trim() || "-"
          ),
          escape(item.email || "-"),
          escape(item.primary_no || "-"),
          escape(item.total_work_exp || "-"),
          escape(item.relevant_experience || "-"),
          escape(item.current_company || "-"),
          escape(item.candidate_location || "-"),
          escape(
            Array.isArray(item.candidate_prefered_location)
              ? item.candidate_prefered_location.join(", ")
              : item.candidate_prefered_location || "-"
          ),
          escape(item.current_ctc || "-"),
          escape(item.expected_ctc || "-"),
          escape(item.notice_period || "-"),
          escape(item.notice_status || "-"),
          escape(
            item.last_working_day ? formatDate(item.last_working_day) : "-"
          ),
          escape(
            item.available_from ? formatDate(item.available_from) : "-"
          ),
          escape(item.offer_in_hand ? "Yes" : "No"),
          escape(item.offered_ctc || "-"),
          escape(
            item.joining_date_of_offer
              ? formatDate(item.joining_date_of_offer)
              : "-"
          ),
          escape(item.date_of_birth ? formatDate(item.date_of_birth) : "-"),
          escape(item.highest_qualification || "-"),
          escape(item.remarks || "-"),
          escape(item.reviewer_remark || "-"),
          escape(item.assessment_status ? "Attempted" : "Not Attempted"),
          escape(
            item.tech_score ? `${item.tech_score}%` : "-"
          ),
          escape(item.assessment_status ? item.red_flag : "-"),
          escape(item.tech_score > 65 ? "Yes" : "No"),
          escape(item.cv_url),
          escape(
            Array.isArray(item.debrief_form)
              ? [...item.debrief_form]
                .sort((a, b) => {
                  const getOrder = (name = "") => {
                    const match = name.match(/\d+/);
                    return match ? Number(match[0]) : Number.MAX_SAFE_INTEGER;
                  };

                  return getOrder(a.round_name) - getOrder(b.round_name);
                })
                .map((round) => {
                  const questions = round.questions?.length
                    ? round.questions.join(" | ")
                    : "-";

                  return `${round.round_name}: ${questions}${round.additional_information
                    ? ` | Additional Info: ${round.additional_information}`
                    : ""
                    }`;
                })
                .join(" || ")
              : "-"
          ),
        ];
      }

      csvRows.push(row.join(","));
    });

    const blob = new Blob([csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `profile_sent_table_${new Date()
      .toISOString()
      .split("T")[0]}.csv`;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setSelectedRows([]);
  };

  /* ─────────────────────────────────────────────
     Drawer logic helpers
  ───────────────────────────────────────────── */
  const isProfileSentLocked = drawerCandidate?.profile_sent === true;
  const isProfileSentNo = drawerCandidate?.profile_sent === false;
  const sourcingDate = drawerCandidate?.created_at ? dayjs(drawerCandidate.created_at) : null;

  const disabledBeforeSourcing = (current) => {
    if (!sourcingDate) return false;
    return current && current.isBefore(sourcingDate, "day");
  };

  const MIN_DATE = dayjs("2026-04-22").startOf("day");
  const disableFutureDates = (current) =>
    current && (current > dayjs().endOf("day") || current < MIN_DATE);

  const MIN_DATE_DEBRIEF = dayjs("2026-06-28").startOf("day");
  const disableDebriefDates = (current) =>
    current && (current > dayjs().endOf("day") || current < MIN_DATE_DEBRIEF);

  const compactViewShowBlue = isCompactViewHovered ? !isCompactView : isCompactView;
  const activeRecruitersShowBlue = activeRecruitersOnlyHovered ? !activeRecruitersOnly : activeRecruitersOnly;

  const isInterviewScheduledStatus = (status) => {
    const value = String(status ?? "").trim();

    return (
      value === "Discussion Round" ||
      /^Interview Scheduled \(R[1-4]\)$/i.test(value)
    );
  };

  const showInterviewDateTime =
    isInterviewScheduledStatus(updateCandidateStatus) &&
    updateCandidateStatus !== drawerCandidate?.candidate_current_status;

  return (
    <Styled.MainDiv>
      <Sidemenu />
      <Styled.ContentWrapDiv>

        {/* ── Top bar ── */}
        <Styled.TopContainer>
          <div className="leftSide" style={{ display: "flex", alignItems: "center", gap: "12px", whiteSpace: "nowrap" }}>
            <span>Profile Sent Tracking</span>

            {/* Active Recruiters Only */}
            <Tooltip title={activeRecruitersOnly ? "Showing active recruiters" : "Showing all recruiters"}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  cursor: "pointer",
                }}
                onClick={() => setActiveRecruitersOnly(!activeRecruitersOnly)}
                onMouseEnter={() => setActiveRecruitersOnlyHovered(true)}
                onMouseLeave={() => setActiveRecruitersOnlyHovered(false)}
              >
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: activeRecruitersShowBlue ? "#1B49EA" : "#374151",
                    whiteSpace: "nowrap",
                    transition: "color 0.15s ease",
                  }}
                >
                  {activeRecruitersOnly}
                </span>

                <div
                  style={{
                    width: "44px",
                    height: "22px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Switch
                    checked={activeRecruitersOnly}
                    onChange={setActiveRecruitersOnly}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: "44px",
                      minWidth: "44px",
                      maxWidth: "44px",
                      height: "22px",
                      minHeight: "22px",
                      background: activeRecruitersOnly ? "#1677ff" : "#D1D5DB",
                      transition: "background 0.15s ease",
                    }}
                  />
                </div>
              </div>
            </Tooltip>
          </div>

          <div className="rightSide" style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "8px 12px",
                background: compactViewShowBlue ? "#E8F0FF" : "#F9FAFB",
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "background 0.15s ease",
              }}
              onClick={() => setIsCompactView(!isCompactView)}
              onMouseEnter={() => setIsCompactViewHovered(true)}
              onMouseLeave={() => setIsCompactViewHovered(false)}
            >
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: compactViewShowBlue ? "#1B49EA" : "#374151",
                  whiteSpace: "nowrap",
                  transition: "color 0.15s ease",
                }}
              >
                Compact View
              </span>

              <div
                style={{
                  width: "44px",
                  height: "22px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Switch
                  checked={isCompactView}
                  onChange={setIsCompactView}
                  onClick={(e) => e.stopPropagation()}   // prevents double toggle
                  style={{
                    width: "44px",
                    minWidth: "44px",
                    maxWidth: "44px",
                    height: "22px",
                    minHeight: "22px",
                    background: compactViewShowBlue ? "#1677ff" : "#D1D5DB",
                    transition: "background 0.15s ease",
                  }}
                />
              </div>
            </div>


            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", padding: "8px 12px", background: showOnlyPending ? "#fef3c7" : "#f9fafb", borderRadius: "8px", border: "1px solid #e5e7eb" }}>

              <input
                type="checkbox"
                checked={showOnlyPending}
                onChange={(e) => {
                  setShowOnlyPending(e.target.checked);
                  if (e.target.checked) {
                    setShowOnlySent(false);
                    setShowOnlyNotSent(false);
                  }
                }}
                style={{ width: "18px", height: "18px", cursor: "pointer", accentColor: "#4F46E5" }}
              />
              <span style={{ fontSize: "14px", fontWeight: 500, color: showOnlyPending ? "#b45309" : "#374151" }}>
                {showOnlyPending ? "Showing Pending" : "Pending"}
              </span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", padding: "8px 12px", background: showOnlySent ? "#dcfce7" : "#f9fafb", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
              <input
                type="checkbox"
                checked={showOnlySent}
                onChange={(e) => {
                  setShowOnlySent(e.target.checked);
                  if (e.target.checked) {
                    setShowOnlyPending(false);
                    setShowOnlyNotSent(false);
                  }
                }}
                style={{ width: "18px", height: "18px", cursor: "pointer", accentColor: "#4F46E5" }}
              />
              <span style={{ fontSize: "14px", fontWeight: 500, color: showOnlySent ? "#166534" : "#374151" }}>
                {showOnlySent ? "Showing Sent (Yes)" : "Profile Sent (Yes)"}
              </span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", padding: "8px 12px", background: showOnlyNotSent ? "#fee2e2" : "#f9fafb", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
              <input
                type="checkbox"
                checked={showOnlyNotSent}
                onChange={(e) => {
                  setShowOnlyNotSent(e.target.checked);
                  if (e.target.checked) {
                    setShowOnlyPending(false);
                    setShowOnlySent(false);
                  }
                }}
                style={{ width: "18px", height: "18px", cursor: "pointer", accentColor: "#4F46E5" }}
              />
              <span style={{ fontSize: "14px", fontWeight: 500, color: showOnlyNotSent ? "#991b1b" : "#374151" }}>
                {showOnlyNotSent ? "Showing Not Sent (No)" : "Profile Sent (No)"}
              </span>
            </label>
            <button onClick={exportToCSV}>Export</button>
          </div>
        </Styled.TopContainer>

        {/* ── Filters ── */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            marginBottom: "20px",
            overflowX: "auto",
            flexWrap: "nowrap",
            paddingBottom: "6px",
            scrollbarWidth: "thin",
            scrollbarColor: "#D1D5DB transparent",
          }}
        >
          <Select showSearch placeholder="Select a account" onChange={(value) => setSelectedCompany(value)} allowClear style={{ minWidth: "180px", flexShrink: 0 }} value={selectedCompany}>
            {companyOptions.map((company) => (
              <Select.Option key={company} value={company}>{company}</Select.Option>
            ))}
          </Select>

          <Select
            showSearch
            placeholder="Select a role"
            onChange={(value) => setSelectedRole(value)}
            allowClear
            style={{ minWidth: "180px", flexShrink: 0 }}
            value={selectedRole}
          >
            {roleOptions.map((role) => (
              <Select.Option key={role} value={role}>
                <Tooltip title={role} placement="top">
                  <span style={{
                    display: "inline-block",
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    verticalAlign: "middle"
                  }}>
                    {role}
                  </span>
                </Tooltip>
              </Select.Option>
            ))}
          </Select>

          <Select
            showSearch
            placeholder="Sub-account"
            onChange={(value) => setSelectedSubAccount(value)}
            allowClear
            style={{ minWidth: "180px", flexShrink: 0 }}
            value={selectedSubAccount}
          >
            {subAccountOptions.map((s) => (
              <Select.Option key={s} value={s}>{s}</Select.Option>
            ))}
          </Select>

          <Select
            showSearch
            placeholder="End client"
            onChange={(value) => setSelectedEndClient(value)}
            allowClear
            style={{ minWidth: "180px", flexShrink: 0 }}
            value={selectedEndClient}
          >
            {endClientOptions.map((e) => (
              <Select.Option key={e} value={e}>{e}</Select.Option>
            ))}
          </Select>
          <DatePicker placeholder="Profile Sent Date" onChange={(date) => setSelectedProfileSentDate(date || null)} format="DD MMM YYYY" allowClear disabledDate={disableFutureDates} style={{ width: "200px", flexShrink: 0 }} />

          <Select placeholder="Assessment status" onChange={(value) => setSelectedAssessmentStatus(value)} allowClear style={{ minWidth: "160px", flexShrink: 0 }} value={selectedAssessmentStatus}>
            <Select.Option value="Attempted">Attempted</Select.Option>
            <Select.Option value="Not Attempted">Not Attempted</Select.Option>
          </Select>

          <Select mode="multiple" placeholder="Candidate Status" onChange={(value) => setSelectedCandidateStatus(value)} allowClear showSearch style={{ minWidth: "200px", flexShrink: 0 }} value={selectedCandidateStatus}>
            {candidateStatusOptions.map((status) => (
              <Select.Option key={status} value={status}>{status}</Select.Option>
            ))}
          </Select>

        <Select showSearch placeholder="Select a recruiter" optionFilterProp="children" onChange={(value) => setSelectedRecruiter(value)} allowClear style={{ minWidth: "180px", flexShrink: 0 }} value={selectedRecruiter}>
  {recruiterOptions
    .filter((item) => !activeRecruitersOnly || item.active === true)
    .map((item) => (
      <Select.Option key={item.recruiter_name} value={item.recruiter_name}>{item.recruiter_name}</Select.Option>
    ))}
</Select>
          <Input
            placeholder="Search by Name / Email"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{ maxWidth: "200px", flexShrink: 0 }}
          />

          <Select
            placeholder="Debrief Status"
            value={selectedDebriefStatus}
            onChange={(value) => setSelectedDebriefStatus(value)}
            allowClear
            style={{ minWidth: "160px", flexShrink: 0 }}
          >
            <Select.Option value="Filled">Filled</Select.Option>
            <Select.Option value="Not Filled">Not Filled</Select.Option>
          </Select>

          <DatePicker
            placeholder="Debrief Date"
            value={selectedDebriefDate}
            onChange={(date) => setSelectedDebriefDate(date || null)}
            format="DD MMM YYYY"
            allowClear
            disabledDate={disableDebriefDates}
            style={{ width: "200px", flexShrink: 0 }}
          />



          <RangePicker
            value={dateRange}
            onChange={(dates) => {
              setDateRange(dates || []);
            }}
            format="DD MMM YYYY"
            placeholder={[
              "Sourced From",
              "Sourced To",
            ]}
            disabledDate={disableFutureDates}
            separator={
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "24px",
                  minWidth: "24px",
                  flexShrink: 0,
                  color: "#6B7280",
                  fontWeight: 600,
                  fontSize: "16px",
                  lineHeight: "1",
                }}
              >
                →
              </span>
            }
            allowClear
            style={{
              width: "262px",
              minWidth: "262px",
              flexShrink: 0,
            }}
          />
        </div>

        <Styled.TableWrap style={{ overflowX: "auto", overflowY: "auto", maxHeight: "calc(100vh - 260px)" }}>
          {showEmptyState ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                marginTop: "100px",
              }}
            >
              <img
                src={require("../../images/Frame.png")}
                alt="No Data"
                style={{ width: "35%", height: "auto" }}
              />
              <h2 style={{ marginTop: "auto" }}>No Result Found</h2>
            </div>
          ) : (
            <Styled.MainTable style={{ transition: "background-color 0.3s ease-in-out" }}>
              <thead style={{ position: "sticky", top: 0, zIndex: 20, background: "#F2F6F8" }}>
                {isCompactView ? (
                  <tr>
                    <th style={{ position: "sticky", left: 0, top: 0, zIndex: 11, background: "#F2F6F8", minWidth: "40px" }}>
                      <input
                        type="checkbox"
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        checked={selectedRows.length === paginatedListings.length && paginatedListings.length > 0}
                      />
                    </th>
                    <th style={{ position: "sticky", left: "0px", top: 0, zIndex: 11, background: "#F2F6F8", minWidth: "200px" }}>
                      Candidate details
                    </th>
                    <th>Account Name</th>
                    <th>Sub Account</th>
                    <th>End Client</th>
                    <th>Date of Sharing</th>
                    <th>Role</th>
                    <th>Recruiter</th>
                    <th>Profile Sent</th>
                    <th>Candidate Status</th>
                    <th>Last Status Updated</th>
                    <th style={{ background: "#F2F6F8" }}>Actions</th>

                  </tr>
                ) : (
                  <tr>
                    <th style={{ position: "sticky", left: 0, top: 0, zIndex: 11, background: "#F2F6F8", minWidth: "40px" }}>
                      <input
                        type="checkbox"
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        checked={selectedRows.length === paginatedListings.length && paginatedListings.length > 0}
                      />
                    </th>
                    <th style={{ position: "sticky", left: "50px", top: 0, zIndex: 11, background: "#F2F6F8", minWidth: "140px" }}>
                      Date of Sourcing
                    </th>
                    <th style={{ position: "sticky", left: "100px", top: 0, zIndex: 11, background: "#F2F6F8", minWidth: "140px" }}>
                      Date of Review
                    </th>
                    <th style={{ position: "sticky", left: "150px", top: 0, zIndex: 11, background: "#F2F6F8", minWidth: "200px" }}>
                      Candidate details
                    </th>
                    <th style={{ background: "#F2F6F8" }}>Profile Sent</th>
                    <th style={{ background: "#F2F6F8" }}>Date of Sharing With Client</th>
                    <th style={{ background: "#F2F6F8" }}>Candidate Current Status</th>
                    <th style={{ background: "#F2F6F8" }}>Client's Feedback</th>
                    <th style={{ background: "#F2F6F8" }}>Client's Remark</th>
                    <th style={{ background: "#F2F6F8" }}>Status Updated Date</th>
                    <th style={{ background: "#F2F6F8" }}>Recruiter Name</th>
                    <th style={{ background: "#F2F6F8" }}>Account Name</th>
                    <th style={{ background: "#F2F6F8" }}>Role</th>
                    <th style={{ background: "#F2F6F8" }}>Total Exp</th>
                    <th style={{ background: "#F2F6F8" }}>Relevant Exp</th>
                    <th style={{ background: "#F2F6F8" }}>Current Company</th>
                    <th style={{ background: "#F2F6F8" }}>Current Location</th>
                    <th style={{ background: "#F2F6F8" }}>Preferred Location</th>
                    <th style={{ background: "#F2F6F8" }}>Current CTC (LPA)</th>
                    <th style={{ background: "#F2F6F8" }}>Expected CTC (LPA)</th>
                    <th style={{ background: "#F2F6F8" }}>Notice Period</th>
                    <th style={{ background: "#F2F6F8" }}>Notice Status</th>
                    <th style={{ background: "#F2F6F8" }}>Last Working Day</th>
                    <th style={{ background: "#F2F6F8" }}>Available From</th>
                    <th style={{ background: "#F2F6F8" }}>Offer in Hand (Y/N)</th>
                    <th style={{ background: "#F2F6F8" }}>Joining Date of Offer</th>
                    <th style={{ background: "#F2F6F8" }}>Date of Birth</th>
                    <th style={{ background: "#F2F6F8" }}>Highest Qualification</th>
                    <th style={{ background: "#F2F6F8" }}>Recruiter's Remarks</th>
                    <th style={{ background: "#F2F6F8" }}>Reviewer's Remarks</th>
                    <th style={{ background: "#F2F6F8" }}>Fitment Score</th>
                    <th style={{ background: "#F2F6F8" }}>Assessment status</th>
                    <th style={{ background: "#F2F6F8" }}>Tech Score</th>
                    <th style={{ background: "#F2F6F8" }}>Red Flag</th>
                    <th style={{ background: "#F2F6F8" }}>Qualify</th>
                    <th style={{ background: "#F2F6F8" }}>Debrief View</th>
                    <th style={{ background: "#F2F6F8" }}>Actions</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {paginatedListings.map((item, index) => {
                  const rowBg = selectedRows.includes(item) ? "#d0e7ff" : "#ffffff";
                  if (isCompactView) {
                    return (
                      <tr key={index} style={{ background: rowBg }}>
                        <td style={{ position: "sticky", left: 0, zIndex: 1, background: rowBg, minWidth: "40px" }}>
                          <input type="checkbox" onChange={() => handleRowSelect(item)} checked={selectedRows.includes(item)} />
                        </td>
                        <td style={{ position: "sticky", left: "0px", zIndex: 1, background: rowBg, minWidth: "200px", transition: "background-color 0.2s ease-in-out" }}>
                          {item.first_name} {item.last_name} <br />
                          {item.email} <br />
                          {item.primary_no}
                        </td>
                        <td>{item.company_name || "-"}</td>
                        <td>{item.sub_account || "-"}</td>
                        <td>{item.end_client || "-"}</td>

                        <td>
                          {item.profile_sent_date
                            ? new Date(item.profile_sent_date).toLocaleDateString()
                            : "-"}
                        </td>

                        <td>{item.role_title || "-"}</td>

                        <td>{item.recruiter_name || "-"}</td>
                        <td>
                          {item.profile_sent === null || item.profile_sent === undefined ? (
                            <span style={{ color: "#b45309", fontWeight: 600, background: "#fef3c7", padding: "2px 8px", borderRadius: "4px", fontSize: "12px" }}>Pending</span>
                          ) : item.profile_sent ? (
                            <span style={{ color: "#166534", fontWeight: 600, background: "#dcfce7", padding: "2px 8px", borderRadius: "4px", fontSize: "12px" }}>Yes</span>
                          ) : (
                            <span style={{ color: "#991b1b", fontWeight: 600, background: "#fee2e2", padding: "2px 8px", borderRadius: "4px", fontSize: "12px" }}>No</span>
                          )}
                        </td>
                        <td>
                          {item.candidate_current_status
                            ? item.candidate_current_status
                            : <span style={{ color: "#b45309", fontWeight: 600 }}>Pending</span>}
                        </td>

                        <td>
                          {item.profile_status_updated_date
                            ? new Date(item.profile_status_updated_date).toLocaleDateString()
                            : "-"}
                        </td>
                        <td>
                          <Styled.IconDiv>
                            <Tooltip title="View Resume">
                              <Styled.ViewButton onClick={() => window.open(item.cv_url, "_blank")} />
                            </Tooltip>
                            {(item.misc_file_url || item.miscellaneous_file) && (
                              <Tooltip title="View Miscellaneous File">
                                <Styled.MiscFileButton
                                  onClick={() =>
                                    window.open(item.misc_file_url || item.miscellaneous_file, "_blank")
                                  }
                                />
                              </Tooltip>
                            )}
                            <Tooltip title="Options">
                              <Dropdown overlay={menu(item)} trigger={["click"]}>
                                <Styled.OptionButton />
                              </Dropdown>
                            </Tooltip>
                          </Styled.IconDiv>
                        </td>
                      </tr>
                    );
                  }
                  return (
                    <tr
                      key={index}
                      style={{
                        backgroundColor: rowBg,
                        transition: "background-color 0.3s ease-in-out",
                      }}
                    >
                      <td style={{ position: "sticky", left: 0, zIndex: 1, background: rowBg, minWidth: "40px" }}>
                        <input type="checkbox" onChange={() => handleRowSelect(item)} checked={selectedRows.includes(item)} />
                      </td>


                      <td style={{ position: "sticky", left: "50px", zIndex: 1, background: rowBg, minWidth: "140px", transition: "background-color 0.2s ease-in-out" }}>
                        {new Date(item.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
                      </td>

                      <td style={{ position: "sticky", left: "100px", zIndex: 1, background: rowBg, minWidth: "140px", transition: "background-color 0.2s ease-in-out" }}>
                        {item.review_status !== "pending"
                          ? new Date(item.updated_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })
                          : "Pending..."}
                      </td>

                      <td style={{ position: "sticky", left: "150px", zIndex: 1, background: rowBg, minWidth: "200px", transition: "background-color 0.2s ease-in-out" }}>
                        {item.first_name} {item.last_name} <br />
                        {item.email} <br />
                        {item.primary_no}
                      </td>

                      <td>
                        {item.profile_sent === null || item.profile_sent === undefined ? (
                          <span style={{ color: "#b45309", fontWeight: 600, background: "#fef3c7", padding: "2px 8px", borderRadius: "4px", fontSize: "12px" }}>Pending</span>
                        ) : item.profile_sent ? (
                          <span style={{ color: "#166534", fontWeight: 600, background: "#dcfce7", padding: "2px 8px", borderRadius: "4px", fontSize: "12px" }}>Yes</span>
                        ) : (
                          <span style={{ color: "#991b1b", fontWeight: 600, background: "#fee2e2", padding: "2px 8px", borderRadius: "4px", fontSize: "12px" }}>No</span>
                        )}
                      </td>
                      <td>
                        {item.profile_sent_date
                          ? new Date(item.profile_sent_date).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })
                          : (item.profile_sent === null || item.profile_sent === undefined)
                            ? <span style={{ color: "#b45309", fontWeight: 600 }}>Pending</span>
                            : "-"}
                      </td>
                      <td>
                        {item.candidate_current_status
                          ? item.candidate_current_status
                          : <span style={{ color: "#b45309", fontWeight: 600 }}>Pending</span>}
                      </td>
                      <td>
                        <Tooltip title={item.client_feedback || "-"} placement="topLeft">
                          <span
                            style={{
                              maxWidth: "150px",
                              display: "inline-block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              cursor: "pointer",
                            }}
                          >
                            {truncateText(item.client_feedback, 25)}
                          </span>
                        </Tooltip>
                      </td>
                      <td>
                        <Tooltip title={item.client_remark || "-"} placement="topLeft">
                          <span style={{ maxWidth: "150px", display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}>
                            {truncateText(item.client_remark, 25)}
                          </span>
                        </Tooltip>
                      </td>
                      <td>{item.profile_status_updated_date ? new Date(item.profile_status_updated_date).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "-"}</td>
                      <td>{item.recruiter_name || "-"}</td>
                      <td>{item.company_name}</td>
                      <td>{item.role_title}</td>
                      <td>{item.total_work_exp ? `${item.total_work_exp}y` : "-"}</td>
                      <td>{item.relevant_experience ? `${item.relevant_experience}y` : "-"}</td>
                      <td>{item.current_company || "-"}</td>
                      <td>{item.candidate_location || "-"}</td>
                      <td>{Array.isArray(item.candidate_prefered_location) ? item.candidate_prefered_location.join(", ") : (item.candidate_prefered_location || "-")}</td>
                      <td>{item.current_ctc ? `${item.current_ctc}` : "-"}</td>
                      <td>{item.expected_ctc ? `${item.expected_ctc}` : "-"}</td>
                      <td>{item.notice_period ? `${item.notice_period}` : "-"}</td>
                      <td>{item.notice_status ? item.notice_status : "-"}</td>
                      <td>{item.last_working_day ? new Date(item.last_working_day).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "-"}</td>
                      <td>{item.available_from ? new Date(item.available_from).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "-"}</td>
                      <td>{item.offer_in_hand ? "Yes - " + item.offered_ctc + " LPA" : "No"}</td>
                      <td>{item.offer_in_hand && item.joining_date_of_offer ? new Date(item.joining_date_of_offer).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "-"}</td>
                      <td>{item.date_of_birth ? new Date(item.date_of_birth).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "-"}</td>
                      <td>{item.highest_qualification || "-"}</td>
                      <td>
                        <Tooltip title={item.remarks || "-"} placement="topLeft">
                          <span style={{ maxWidth: "150px", display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}>
                            {truncateText(item.remarks, 25)}
                          </span>
                        </Tooltip>
                      </td>
                      <td>
                        <Tooltip title={item.reviewer_remark || "-"} placement="topLeft">
                          <span style={{ maxWidth: "150px", display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}>
                            {truncateText(item.reviewer_remark, 25)}
                          </span>
                        </Tooltip>
                      </td>
                      <td>{item.fitment_score ? `${item.fitment_score}` : "-"}</td>
                      <td>{item.assessment_status?.toUpperCase() === "SUBMITTED" ? "Submitted" : "Not Submitted"}</td>
                      <td>{item.assessment_status?.toUpperCase() === "SUBMITTED" && item.tech_score != null ? `${item.tech_score}%` : "-"}</td>
                      <td style={{
                        color: item.assessment_status?.toUpperCase() !== "SUBMITTED" ? "inherit" : item.red_flag > 0 ? "red" : "green",
                        fontWeight: item.assessment_status?.toUpperCase() === "SUBMITTED" ? "bold" : "normal",
                        textAlign: "center",
                      }}>
                        {item.assessment_status?.toUpperCase() !== "SUBMITTED" ? "-" : item.red_flag ?? 0}
                      </td>
                      <td style={{
                        backgroundColor: item.assessment_status?.toUpperCase() !== "SUBMITTED" ? "transparent" : item.tech_score >= 65 ? "lightgreen" : "lightcoral",
                        fontWeight: item.assessment_status?.toUpperCase() === "SUBMITTED" ? "bold" : "normal",
                        textAlign: "center",
                      }}>
                        {item.assessment_status?.toUpperCase() !== "SUBMITTED" ? "-" : item.tech_score >= 65 ? "Yes" : "No"}
                      </td>
                      <td>
                        {(() => {
                          const sortedDebrief = [
                            ...(
                              typeof item.debrief_form === "string"
                                ? JSON.parse(item.debrief_form)
                                : item.debrief_form || []
                            ),
                          ].sort((a, b) => {
                            const roundA = parseInt(
                              (a.round_name || "").match(/\d+/)?.[0] || "999"
                            );
                            const roundB = parseInt(
                              (b.round_name || "").match(/\d+/)?.[0] || "999"
                            );
                            return roundA - roundB;
                          });

                          const noReasonList = [
                            ...(
                              typeof item.debrief_no_reason === "string"
                                ? JSON.parse(item.debrief_no_reason)
                                : item.debrief_no_reason || []
                            ),
                          ];

                          const getRoundNumber = (roundName) =>
                            parseInt((roundName || "").match(/\d+/)?.[0] || "999", 10);

                          const combinedRounds = [
                            ...sortedDebrief.map((round) => ({
                              type: "yes",
                              roundNumber: getRoundNumber(round.round_name),
                              data: round,
                            })),
                            ...noReasonList
                              .filter(
                                (round) =>
                                  !sortedDebrief.some(
                                    (yesRound) =>
                                      getRoundNumber(yesRound.round_name) ===
                                      getRoundNumber(round.round_name),
                                  ),
                              )
                              .map((round) => ({
                                type: "no",
                                roundNumber: getRoundNumber(round.round_name),
                                data: round,
                              })),
                          ].sort((a, b) => a.roundNumber - b.roundNumber);

                          return (
                            <>
                              {combinedRounds.length > 0 ? (
                                <Dropdown
                                  menu={{
                                    items: [
                                      {
                                        key: "content",
                                        label: (
                                          <div
                                            style={{
                                              maxWidth: "320px",
                                              padding: "10px",
                                              position: "relative",
                                            }}
                                          >
                                            <Button
                                              type="text"
                                              size="small"
                                              icon={<CopyOutlined />}
                                              onClick={(e) => {
                                                e.stopPropagation();

                                                const debriefText = sortedDebrief
                                                  .map(
                                                    (round) =>
                                                      `${round.round_name}\n${round.questions?.join("\n")}\n${round.additional_information || ""
                                                      }`
                                                  )
                                                  .join("\n\n");

                                                copyDebriefToClipboard(debriefText);
                                              }}
                                              style={{
                                                position: "absolute",
                                                top: "6px",
                                                right: "6px",
                                                padding: 0,
                                                width: "24px",
                                                height: "24px",
                                                minWidth: "24px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: "#1b49ea",
                                              }}
                                            />

                                            <div
                                              style={{
                                                fontSize: "12px",
                                                fontWeight: 600,
                                                color: "#111827",
                                                marginBottom: "8px",
                                                paddingRight: "30px",
                                              }}
                                            >
                                              Debrief Details
                                            </div>

                                            <div
                                              style={{
                                                maxHeight: "300px",
                                                overflowY: "auto",
                                                paddingRight: "4px",
                                              }}
                                            >
                                              {combinedRounds.map((entry, index) =>
                                                entry.type === "yes" ? (
                                                  <div
                                                    key={index}
                                                    style={{
                                                      marginBottom: "14px",
                                                      background: "#f8fafc",
                                                      border: "1px solid #e5e7eb",
                                                      borderRadius: "10px",
                                                      padding: "12px",
                                                      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        marginBottom: "10px",
                                                        borderBottom: "1px solid #e5e7eb",
                                                        paddingBottom: "8px",
                                                      }}
                                                    >
                                                      <div
                                                        style={{
                                                          fontWeight: 700,
                                                          fontSize: "13px",
                                                          color: "#1b49ea",
                                                        }}
                                                      >
                                                        {entry.data.round_name}
                                                      </div>

                                                      <div
                                                        style={{
                                                          fontSize: "11px",
                                                          color: "#6b7280",
                                                        }}
                                                      >
                                                        {entry.data.submitted_at
                                                          ? new Date(
                                                            entry.data.submitted_at
                                                          ).toLocaleDateString()
                                                          : "No Date"}
                                                      </div>
                                                    </div>

                                                    <div
                                                      style={{
                                                        fontSize: "11px",
                                                        fontWeight: 600,
                                                        color: "#6b7280",
                                                        marginBottom: "8px",
                                                        textTransform: "uppercase",
                                                        letterSpacing: "0.5px",
                                                      }}
                                                    >
                                                      Questions Asked
                                                    </div>

                                                    {entry.data.questions?.map((question, qIndex) => (
                                                      <div
                                                        key={qIndex}
                                                        style={{
                                                          padding: "8px 10px",
                                                          background: "#fff",
                                                          border: "1px solid #e5e7eb",
                                                          borderRadius: "6px",
                                                          marginBottom: "6px",
                                                          fontSize: "12px",
                                                          color: "#111827",
                                                          wordBreak: "break-word",
                                                          whiteSpace: "normal",
                                                          lineHeight: "1.5",
                                                        }}
                                                      >
                                                        <strong>{qIndex + 1}.</strong> {question}
                                                      </div>
                                                    ))}

                                                    {entry.data.additional_information && (
                                                      <div
                                                        style={{
                                                          marginTop: "12px",
                                                          background: "#eef4ff",
                                                          borderLeft: "4px solid #1b49ea",
                                                          borderRadius: "6px",
                                                          padding: "10px",
                                                        }}
                                                      >
                                                        <div
                                                          style={{
                                                            fontSize: "11px",
                                                            fontWeight: 700,
                                                            color: "#1b49ea",
                                                            marginBottom: "4px",
                                                            textTransform: "uppercase",
                                                          }}
                                                        >
                                                          Additional Information
                                                        </div>

                                                        <div
                                                          style={{
                                                            fontSize: "12px",
                                                            color: "#374151",
                                                            lineHeight: "1.5",
                                                          }}
                                                        >
                                                          {entry.data.additional_information}
                                                        </div>
                                                      </div>
                                                    )}
                                                  </div>
                                                ) : (
                                                  <div
                                                    key={index}
                                                    style={{
                                                      marginBottom: "14px",
                                                      background: "#fef2f2",
                                                      border: "1px solid #fca5a5",
                                                      borderRadius: "10px",
                                                      padding: "12px",
                                                      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        marginBottom: "10px",
                                                        borderBottom: "1px solid #fca5a5",
                                                        paddingBottom: "8px",
                                                      }}
                                                    >
                                                      <div
                                                        style={{
                                                          fontWeight: 700,
                                                          fontSize: "13px",
                                                          color: "#dc2626",
                                                        }}
                                                      >
                                                        {entry.data.round_name}
                                                      </div>

                                                      <div
                                                        style={{
                                                          fontSize: "11px",
                                                          color: "#6b7280",
                                                        }}
                                                      >
                                                        {entry.data.submitted_at
                                                          ? new Date(
                                                            entry.data.submitted_at
                                                          ).toLocaleDateString()
                                                          : "No Date"}
                                                      </div>
                                                    </div>

                                                    <div
                                                      style={{
                                                        fontSize: "11px",
                                                        fontWeight: 700,
                                                        color: "#dc2626",
                                                        marginBottom: "6px",
                                                        textTransform: "uppercase",
                                                        letterSpacing: "0.5px",
                                                      }}
                                                    >
                                                      NO
                                                    </div>

                                                    <div
                                                      style={{
                                                        padding: "8px 10px",
                                                        background: "#fff",
                                                        border: "1px solid #fca5a5",
                                                        borderRadius: "6px",
                                                        fontSize: "12px",
                                                        color: "#111827",
                                                        wordBreak: "break-word",
                                                        whiteSpace: "normal",
                                                        lineHeight: "1.5",
                                                      }}
                                                    >
                                                      <strong>Reason:</strong>{" "}
                                                      {entry.data.reason || "No reason provided"}
                                                    </div>
                                                  </div>
                                                ),
                                              )}
                                            </div>
                                          </div>
                                        ),
                                        disabled: true,
                                      },
                                    ],
                                  }}
                                  trigger={["click"]}
                                  placement="bottomRight"
                                >
                                  <Button
                                    type="text"
                                    size="small"
                                    onClick={(e) => e.preventDefault()}
                                    style={{
                                      fontSize: "11px",
                                      color: "#1b49ea",
                                      padding: "2px 4px",
                                      height: "24px",
                                      minWidth: "auto",
                                      fontWeight: 500,
                                      display: "flex",
                                      alignItems: "center",
                                      border: "1px solid #1b49ea",
                                      borderRadius: "6px",
                                      background: "#fff",
                                    }}
                                  >
                                    <span>View Debrief</span>
                                    <DownOutlined
                                      style={{
                                        fontSize: "9px",
                                        display: "flex",
                                        alignItems: "center",
                                        marginLeft: "4px",
                                      }}
                                    />
                                  </Button>
                                </Dropdown>
                              ) : (
                                "-"
                              )}
                            </>
                          );
                        })()}
                      </td>
                      <td>
                        <Styled.IconDiv>
                          <Tooltip title="View Resume">
                            <Styled.ViewButton onClick={() => window.open(item.cv_url, "_blank")} />
                          </Tooltip>
                          {(item.misc_file_url || item.miscellaneous_file) && (
                            <Tooltip title="View Miscellaneous File">
                              <Styled.MiscFileButton
                                onClick={() =>
                                  window.open(item.misc_file_url || item.miscellaneous_file, "_blank")
                                }
                              />
                            </Tooltip>
                          )}
                          <Tooltip title="Options">
                            <Dropdown overlay={menu(item)} trigger={["click"]}>
                              <Styled.OptionButton />
                            </Dropdown>
                          </Tooltip>
                        </Styled.IconDiv>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Styled.MainTable>
          )}
        </Styled.TableWrap>

        {/* ── Pagination ── */}
        {!showEmptyState && (
          <div style={paginationStyles.container}>
            <div style={paginationStyles.leftSection}>
              <span>Show</span>
              <Styled.PaginationSelectWrapper>
                <Select
                  defaultValue={10}
                  size="small"
                  dropdownMatchSelectWidth={false}
                  onChange={(value) => {
                    setItemsPerPage(value);
                    setCurrentPage(1);
                  }}
                >
                  <Select.Option value={10}>10</Select.Option>
                  <Select.Option value={20}>20</Select.Option>
                  <Select.Option value={50}>50</Select.Option>
                </Select>
              </Styled.PaginationSelectWrapper>
              <span>per page</span>
              <span style={{ marginLeft: "12px", color: "#9CA3AF" }}>|</span>
              <span style={{ marginLeft: "12px" }}>
                {(currentPage - 1) * itemsPerPage + 1}–
                {Math.min(currentPage * itemsPerPage, totalCount)} of{" "}
                {totalCount} results
              </span>
            </div>
            <div style={paginationStyles.pageControls}>
              <button
                style={paginationStyles.navBtn(currentPage === 1)}
                onClick={() => setCurrentPage((p) => p - 1)}
                disabled={currentPage === 1}
              >
                ← Prev
              </button>
              {buildPageNumbers().map((page, idx) =>
                page === "..." ? (
                  <span key={`ellipsis-${idx}`} style={paginationStyles.ellipsis}>…</span>
                ) : (
                  <button
                    key={page}
                    style={paginationStyles.pageBtn(page === currentPage)}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                )
              )}
              <button
                style={paginationStyles.navBtn(currentPage === totalPages)}
                onClick={() => setCurrentPage((p) => p + 1)}
                disabled={currentPage === totalPages}
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {/* ── View Profile Modal ── */}
        <Modal
          visible={isViewModalVisible}
          onCancel={handleCancel}
          footer={[<Button key="back" onClick={handleCancel}>Close</Button>]}
        >
          <div><ProfileComp profile={profile} /></div>
        </Modal>

        {/* ── Reviewer Remark Modal ── */}
        <Modal
          visible={isRemarkModalVisible}
          onCancel={() => setIsRemarkModalVisible(false)}
          footer={[
            <Button key="back" onClick={() => setIsRemarkModalVisible(false)}>Cancel</Button>,
            <Button key="submit" type="primary" onClick={updateStatusWithRemark}>Submit</Button>,
          ]}
        >
          <h3>Reviewer's Remark</h3>
          <Input.TextArea value={reviewerRemark} onChange={(e) => setReviewerRemark(e.target.value)} rows={4} placeholder="Enter your remark here" />
        </Modal>

        {/* ── Update Profile Info Drawer ── */}
        <Drawer
          title={
            <div>
              <div style={{ fontWeight: 700, fontSize: "16px" }}>Update Profile Info</div>
              {drawerCandidate && (
                <div style={{ fontWeight: 400, fontSize: "13px", color: "#888", marginTop: "2px" }}>
                  {drawerCandidate.first_name} {drawerCandidate.last_name} &nbsp;·&nbsp; {drawerCandidate.company_name} &nbsp;·&nbsp; {drawerCandidate.role_title}
                </div>
              )}
            </div>
          }
          placement="right"
          width={420}
          onClose={closeDrawer}
          visible={isDrawerVisible}
          footer={
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <Button onClick={closeDrawer}>Cancel</Button>
              <Button type="primary" loading={updateLoading} onClick={handleUpdateSubmit}>
                Save Changes
              </Button>
            </div>
          }
        >
          <div style={{ fontSize: "12px", fontWeight: 600, color: "#999", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "18px" }}>
            Profile Tracking Fields
          </div>

          {/* ── PROFILE SENT (Always Visible) ── */}
          <div style={{ marginBottom: "24px" }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "14px", marginBottom: "8px", color: "#333" }}>Profile Sent</label>
            <Select
              placeholder="Select Yes / No"
              value={updateProfileSent}
              onChange={(val) => {
                setUpdateProfileSent(val);
                if (val === false) {
                  setUpdateProfileSentDate(null);
                  setUpdateCandidateStatus(null);
                  setUpdateCandidateStatusChangedDate(null);
                  setClientFeedback("");
                  setUpdateClientRemark("");
                }
              }}
              allowClear
              disabled={isProfileSentLocked || isProfileSentNo}
              style={{ width: "100%", opacity: (isProfileSentLocked || isProfileSentNo) ? 0.65 : 1 }}
            >
              <Select.Option value={true}>Yes</Select.Option>
              <Select.Option value={false}>No</Select.Option>
            </Select>
            {isProfileSentLocked && !isProfileSentNo && (
              <div style={{ fontSize: "12px", color: "#f5a623", marginTop: "5px" }}>
                ⚠ Profile has already been sent — this field cannot be changed.
              </div>
            )}
            {isProfileSentNo && (
              <div style={{ fontSize: "12px", color: "#dc2626", marginTop: "5px", fontWeight: 600 }}>
                ⚠ Profile Sent is set to No — entire form is locked and cannot be edited.
              </div>
            )}
          </div>

          {/* ── CONDITIONAL RENDERING: Show these fields only when Profile Sent is NOT "No" ── */}
          {updateProfileSent !== false && (
            <>
              {/* ── PROFILE SENT DATE ── */}
              <div style={{ marginBottom: "24px" }}>
                <label style={{ display: "block", fontWeight: 600, fontSize: "14px", marginBottom: "8px", color: "#333" }}>
                  Profile Sent Date
                  {sourcingDate && (
                    <span style={{ fontWeight: 400, fontSize: "12px", color: "#6B7280", marginLeft: "6px" }}>
                      (earliest: {sourcingDate.format("DD MMM YYYY")})
                    </span>
                  )}
                </label>
                <DatePicker
                  placeholder="Select date"
                  value={updateProfileSentDate}
                  onChange={(date) => setUpdateProfileSentDate(date || null)}
                  format="DD MMM YYYY"
                  allowClear
                  disabledDate={disabledBeforeSourcing}
                  disabled={isProfileSentLocked || isProfileSentNo}
                  style={{ width: "100%", opacity: (isProfileSentLocked || isProfileSentNo) ? 0.65 : 1 }}
                />
                {!isProfileSentLocked && !isProfileSentNo && sourcingDate && (
                  <div style={{ fontSize: "12px", color: "#6B7280", marginTop: "4px" }}>
                    📅 Dates before sourcing date ({sourcingDate.format("DD MMM YYYY")}) are not selectable.
                  </div>
                )}
              </div>

              {/* ── CANDIDATE CURRENT STATUS ── */}
              <div style={{ marginBottom: "24px" }}>
                <label style={{ display: "block", fontWeight: 600, fontSize: "14px", marginBottom: "8px", color: "#333" }}>Candidate Current Status</label>
                <Select
                  placeholder="Select status"
                  value={updateCandidateStatus}
                  onChange={(val) => {
                    setUpdateCandidateStatus(val);
                    if (val) {
                      setUpdateCandidateStatusChangedDate(
                        resolveStatusChangedDate(val, drawerCandidate?.status_history)
                      );
                    } else {
                      setUpdateCandidateStatusChangedDate(null);
                    }
                    if (!interviewStatuses.includes(val)) {
                      setInterviewDateTime(null);
                    }
                  }}
                  allowClear
                  showSearch
                  disabled={isProfileSentNo}
                  style={{ width: "100%", opacity: isProfileSentNo ? 0.65 : 1 }}
                >
                  {candidateStatusOptions.map((status) => {
                    const isGlobalAvailable =
                      statusFlow.ALWAYS_AVAILABLE.includes(status);

                    const isDisabled =
                      !allowedStatuses.includes(status) ||
                      (
                        !isCurrentStatusGlobal &&
                        !isGlobalAvailable &&
                        fullCandidateStatusOptions.indexOf(status) < currentStatusIndex
                      );

                    return (
                      <Select.Option
                        key={status}
                        value={status}
                        disabled={isDisabled}
                      >
                        {status}
                      </Select.Option>
                    );
                  })}
                </Select>
              </div>

              {showInterviewDateTime && (
                <div style={{ marginBottom: "24px" }}>
                  <label
                    style={{
                      display: "block",
                      fontWeight: 600,
                      fontSize: "14px",
                      marginBottom: "8px",
                      color: "#333",
                    }}
                  >
                    Interview Date & Time
                  </label>

                  <DatePicker
                    showTime={{
                      format: "HH:mm",
                      showSecond: false,
                      minuteStep: 5,
                    }}
                    format="DD-MM-YYYY HH:mm"
                    style={{ width: "100%", height: "40px" }}
                    disabledDate={(current) =>
                      current && current < dayjs().startOf("day")
                    }
                    popupStyle={{
                      width: "420px",
                      right: "10px",
                    }}
                    value={interviewDateTime}
                    onChange={(date) =>
                      setInterviewDateTime(date)
                    }
                  />
                </div>
              )}

              {/* ── STATUS CHANGED DATE ── */}
              <div style={{ marginBottom: "24px" }}>
                <label style={{ display: "block", fontWeight: 600, fontSize: "14px", marginBottom: "8px", color: "#333" }}>
                  Status Changed Date
                  {sourcingDate && (
                    <span style={{ fontWeight: 400, fontSize: "12px", color: "#6B7280", marginLeft: "6px" }}>
                      (earliest: {sourcingDate.format("DD MMM YYYY")})
                    </span>
                  )}
                </label>
                <DatePicker
                  placeholder="Select date"
                  value={updateCandidateStatusChangedDate}
                  onChange={(date) => {
                    setUpdateCandidateStatusChangedDate(date || null);
                    if (!date) {
                      setUpdateCandidateStatus(null);
                    }
                  }}
                  format="DD MMM YYYY"
                  allowClear
                  disabledDate={disabledBeforeSourcing}
                  disabled={isProfileSentNo || !updateCandidateStatus}
                  style={{ width: "100%", opacity: isProfileSentNo || !updateCandidateStatus ? 0.65 : 1 }}
                />
                {!isProfileSentNo && updateCandidateStatus && (
                  <div style={{ fontSize: "12px", color: "#6B7280", marginTop: "4px" }}>
                    📅 Uses the saved date from status history when available, otherwise today — you can adjust this date.
                  </div>
                )}
                {!isProfileSentNo && !updateCandidateStatus && (
                  <div style={{ fontSize: "12px", color: "#6B7280", marginTop: "4px" }}>
                    Select a candidate status first to set the status changed date.
                  </div>
                )}
              </div>

              {/* ── CLIENT'S FEEDBACK (Only visible when Profile Sent !== false) ── */}
              <div style={{ borderTop: "1px solid #f0f0f0", marginTop: "8px", paddingTop: "20px" }}>
                <div style={{ marginBottom: "24px" }}>
                  <label style={{ display: "block", fontWeight: 600, fontSize: "14px", marginBottom: "8px", color: "#333" }}>Client's Feedback</label>
                  <Input.TextArea
                    placeholder="Enter client's feedback"
                    value={client_feedback}
                    onChange={(e) => {
                      setClientFeedback(e.target.value);
                    }}
                    rows={4}
                    disabled={isProfileSentNo}
                    style={{ width: "100%", resize: "vertical", opacity: isProfileSentNo ? 0.65 : 1 }}
                  />
                </div>
              </div>
            </>
          )}

          {/* ── CLIENT'S REMARK (Always Visible) ── */}
          <div style={{ borderTop: updateProfileSent === false ? "1px solid #f0f0f0" : "none", marginTop: updateProfileSent === false ? "8px" : "0", paddingTop: updateProfileSent === false ? "20px" : "0" }}>
            <div style={{ marginBottom: "24px" }}>
              <label style={{ display: "block", fontWeight: 600, fontSize: "14px", marginBottom: "8px", color: "#333" }}>Client's Remark</label>
              <Input.TextArea
                placeholder="Enter client's remark"
                value={updateClientRemark}
                onChange={(e) => setUpdateClientRemark(e.target.value)}
                rows={4}
                disabled={isProfileSentNo}
                style={{ width: "100%", resize: "vertical", opacity: isProfileSentNo ? 0.65 : 1 }}
              />
            </div>
          </div>

          {/* ── CURRENT VALUES SECTION (Conditional Rendering) ── */}
          {drawerCandidate && (
            <div style={{ marginTop: "8px", padding: "14px 16px", background: "#f9f9f9", borderRadius: "8px", border: "1px solid #eee" }}>
              <div style={{ fontSize: "12px", fontWeight: 600, color: "#999", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "10px" }}>
                Current Values
              </div>
              <div style={{ fontSize: "13px", color: "#555", display: "flex", flexDirection: "column", gap: "6px" }}>
                <div>
                  <span style={{ fontWeight: 600 }}>Profile Sent:</span>{" "}
                  {updateProfileSent === null || updateProfileSent === undefined ? (
                    <span style={{ color: "#b45309", fontWeight: 600 }}>Pending</span>
                  ) : updateProfileSent ? "Yes" : "No"}
                </div>

                {updateProfileSent !== false && (
                  <>
                    <div>
                      <span style={{ fontWeight: 600 }}>Sent Date:</span>{" "}
                      {updateProfileSentDate
                        ? updateProfileSentDate.toDate().toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })
                        : drawerCandidate.profile_sent_date
                          ? new Date(drawerCandidate.profile_sent_date).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })
                          : (updateProfileSent === null || updateProfileSent === undefined)
                            ? <span style={{ color: "#b45309", fontWeight: 600 }}>Pending</span>
                            : "—"}
                    </div>

                    <div>
                      <span style={{ fontWeight: 600 }}>Status:</span>{" "}
                      {updateCandidateStatus
                        ? updateCandidateStatus
                        : drawerCandidate.candidate_current_status
                          ? drawerCandidate.candidate_current_status
                          : <span style={{ color: "#b45309", fontWeight: 600 }}>Pending</span>}
                    </div>

                    <div>
                      <span style={{ fontWeight: 600 }}>Status Changed Date:</span>{" "}
                      {(() => {
                        if (updateCandidateStatusChangedDate) {
                          return updateCandidateStatusChangedDate.toDate().toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
                        }
                        const savedStatusDate =
                          getStatusChangedDateFromHistory(
                            drawerCandidate.candidate_current_status,
                            drawerCandidate.status_history
                          ) || (drawerCandidate.candidate_current_status_time
                            ? dayjs(drawerCandidate.candidate_current_status_time)
                            : null);
                        return savedStatusDate
                          ? savedStatusDate.toDate().toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })
                          : updateCandidateStatus || drawerCandidate.candidate_current_status
                            ? "—"
                            : <span style={{ color: "#b45309", fontWeight: 600 }}>Pending</span>;
                      })()}
                    </div>

                    <div>
                      <span style={{ fontWeight: 600 }}>Client's Feedback:</span> {client_feedback || drawerCandidate.client_feedback || "—"}
                    </div>
                  </>
                )}

                <div>
                  <span style={{ fontWeight: 600 }}>Client's Remark:</span> {updateClientRemark || drawerCandidate.client_remark || "—"}
                </div>
              </div>
            </div>
          )}
        </Drawer>

      </Styled.ContentWrapDiv>

    </Styled.MainDiv>
  );
};

export default ProfileSentTable;